// src/components/Theme/DigitRenderer.js

import React from 'react';
import { Rect, Line, Group } from 'react-konva';
import PropTypes from 'prop-types';

// Define the FONT_DIGITS_3x5 array
export const FONT_DIGITS_3x5 = [
    // Definitions as per your existing code...
    // 0 to 9 as defined in your initial code
    [ // 0
        [3, 1, 2],
        [1, 0, 1],
        [1, 0, 1],
        [1, 0, 1],
        [4, 1, 5],
    ],
    [ // 1
        [0, 0, 2],
        [0, 0, 1],
        [0, 0, 1],
        [0, 0, 1],
        [0, 0, 1],
    ],
    [ // 2
        [1, 1, 2],
        [0, 0, 1],
        [3, 1, 5],
        [1, 0, 0],
        [1, 1, 1],
    ],
    [ // 3
        [1, 1, 2],
        [0, 0, 1],
        [0, 1, 1],
        [0, 0, 1],
        [1, 1, 5],
    ],
    [ // 4
        [1, 0, 1],
        [1, 0, 1],
        [4, 1, 1],
        [0, 0, 1],
        [0, 0, 1],
    ],
    [ // 5
        [1, 1, 1],
        [1, 0, 0],
        [1, 1, 2],
        [0, 0, 1],
        [1, 1, 5],
    ],
    [ // 6
        [3, 1, 1],
        [1, 0, 0],
        [1, 1, 2],
        [1, 0, 1],
        [4, 1, 5],
    ],
    [ // 7
        [1, 1, 2],
        [0, 0, 1],
        [0, 0, 1],
        [0, 0, 1],
        [0, 0, 1],
    ],
    [ // 8
        [3, 1, 2],
        [1, 0, 1],
        [1, 1, 1],
        [1, 0, 1],
        [4, 1, 5],
    ],
    [ // 9
        [3, 1, 2],
        [1, 0, 1],
        [4, 1, 1],
        [0, 0, 1],
        [1, 1, 5],
    ],
];

export const SPECIAL_CHARACTERS = {
    '.': (x, y, size, color) => (
        <Rect
            key={`dot-${x}-${y}`}
            x={x}
            y={y + 4 * size}
            width={size}
            height={size}
            fill={color}
        />
    ),
    ':': (x, y, size, color) => (
        <Group key={`colon-${x}-${y}`}>
            <Rect
                x={x}
                y={y + 2 * size}
                width={size}
                height={size}
                fill={color}
            />
            <Rect
                x={x}
                y={y + 4 * size}
                width={size}
                height={size}
                fill={color}
            />
        </Group>
    ),
    '-': (x, y, size, color) => (
        <Group key={`dash-${x}-${y}`}>
            <Rect
                x={x}
                y={y + size * 2}
                width={size * 2}
                height={size}
                fill={color}
            />
        </Group>
    ),
    '/': (x, y, size, color) => (
        <Line
            key={`slash-${x}-${y}`}
            points={[x, y + size * 5, x + size * 3, y]}
            stroke={color}
            strokeWidth={size / 2}
            lineCap="round"
            lineJoin="round"
        />
    ),
    ' ': (x, y, size, color) => (
        <Rect
            key={`space-${x}-${y}`}
            x={x}
            y={y}
            width={size * 3}
            height={size * 5}
            fill="transparent" // Assuming you want it transparent
        />
    ),
};

// Digit Component
export const Digit = ({ digit, x, y, size, color }) => {
    if (digit < 0 || digit > 9) return null; // Invalid digit

    const rows = FONT_DIGITS_3x5[digit];
    const elementsToRender = [];

    rows.forEach((row, rowIndex) => {
        row.forEach((pixel, colIndex) => {
            if (pixel !== 0) {
                const pixelX = x + colIndex * size;
                const pixelY = y + rowIndex * size;

                let cornerRadius = 0;
                switch (pixel) {
                    case 1:
                        cornerRadius = 0;
                        break;
                    case 2:
                        cornerRadius = [0, size / 2, 0, 0]; // Top-right rounded
                        break;
                    case 3:
                        cornerRadius = [size / 2, 0, 0, 0]; // Top-left rounded
                        break;
                    case 4:
                        cornerRadius = [0, 0, 0, size / 2]; // Bottom-left rounded
                        break;
                    case 5:
                        cornerRadius = [0, 0, size / 2, 0]; // Bottom-right rounded
                        break;
                    default:
                        cornerRadius = 0;
                        break;
                }

                elementsToRender.push(
                    <Rect
                        key={`digit-${digit}-pixel-${rowIndex}-${colIndex}`}
                        x={pixelX}
                        y={pixelY}
                        width={size}
                        height={size}
                        fill={color}
                        cornerRadius={cornerRadius}
                    />
                );
            }
        });
    });

    return <Group>{elementsToRender}</Group>;
};

Digit.propTypes = {
    digit: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
};

// NumberRenderer Component
export const NumberRenderer = ({
    number,
    x,
    y,
    size,
    color,
    spacing,
}) => {
    const elementsToRender = [];
    let cursorX = x;
    const numberArray = number.split('');
    const height = size * 5;

    for (let i = numberArray.length - 1; i >= 0; i--) {
        const ch = numberArray[i];

        let width = 0;

        if (ch >= '0' && ch <= '9') {
            width = size * 3;
        } else if (SPECIAL_CHARACTERS[ch]) {
            if (ch === '.' || ch === ':') {
                width = size;
            } else if (ch === '-') {
                width = size * 2;
            } else if (ch === ' ') {
                width = size * 3;
            } else if (ch === '/') {
                width = size * 3;
            } else {
                width = size * 3; // Default width
            }
        } else {
            // Handle unknown character if needed
            continue;
        }

        // Decrement cursorX before rendering
        cursorX -= width;

        if (i < numberArray.length - 1) {
            // Not the last character, so subtract spacing
            cursorX -= spacing;
        }

        if (ch >= '0' && ch <= '9') {
            elementsToRender.push(
                <Digit
                    key={`digit-${i}`}
                    digit={parseInt(ch, 10)}
                    x={cursorX}
                    y={y}
                    size={size}
                    color={color}
                />
            );
        } else if (SPECIAL_CHARACTERS[ch]) {
            elementsToRender.push(
                <Group key={`special-${i}`}>
                    {SPECIAL_CHARACTERS[ch](cursorX, y, size, color)}
                </Group>
            );
        }
    }

    return <Group>{elementsToRender}</Group>;
};



NumberRenderer.propTypes = {
    number: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    spacing: PropTypes.number,
};

NumberRenderer.defaultProps = {
    spacing: 2,
};
